<template>
	<o-page class="organization-usage">
		<o-section v-if="!setup.self.hasOrganization">
			<o-alert
				color="info"
				title="Vous ne faites actuellement partie d'aucune organisation"
				center>
				Découvrez les avantages d'une organisation sur <b>Armado</b><br>
				et créer sans plus tarder la vôtre en cliquant ci-dessous
			</o-alert>
		</o-section>

		<o-section v-if="display.includes('hero')">
			<div class="organization-usage__hero">
				<div
					v-if="!setup.ui.onLayoutNavBottom"
					class="organization-usage__hero-poster">
					<img
						src="@Img/organization-usage.svg"
						alt="Fonctionnement des organisations">
				</div>

				<div class="organization-usage__hero-content">
					<h2>Regroupez vos sociétés, gérez vos utilisateurs et plus encore !</h2>
					<p>
						Que vous travailliez sur une seule société ou tout un groupe,
						gérer les droits de vos utilisateurs n'a jamais été aussi simple.
					</p>
					<p>
						Organisation, rôles et tags vont vous apporter une vision d'ensemble
						et des outils indispensables à votre gestion de l'intérim.
					</p>
					<o-button
						v-if="!setup.self.hasOrganization && setup.self.can('OrganizationCreate')"
						class="mt-sm"
						color="brand"
						@click="setup.openOrganizationReleaseNoteModal()">
						Créer mon organisation
					</o-button>
				</div>
			</div>
		</o-section>

		<o-section v-if="display.includes('all-clear')">
			<div class="organization-usage__hero-section">
				<div class="flex ai-fs g-md">
					<div style="flex:2">
						<p>
							<o-label
								color="success"
								size="lg"
								outline>
								Tout est en ordre !
							</o-label>
						</p>
						<h3
							class="organization-usage__hero-title"
							style="max-width:none">
							Il n'y aucune action à effectuer pour le moment
						</h3>
						<p
							class="organization-usage__hero-subtitle"
							style="max-width:none">
							Toutes vos sociétés sont liées à l'organisation.<br>
							Tous vos utilisateurs ont au moins un rôle.<br>
							Et tous vos rôles sont liés à au moins un utilisateur.
						</p>
					</div>
					<img
						src="@/assets/img/OrganizationClean.svg"
						style="flex:1">
				</div>
			</div>
		</o-section>

		<o-section v-if="display.includes('roles')">
			<div class="organization-usage__section">
				<div class="row row--grid">
					<div class="col-sm-6">
						<o-label
							color="pink"
							size="lg"
							outline>
							Les Rôles
						</o-label>

						<h4>La flexibilité des droits au service de votre groupe</h4>
					</div>
				</div>

				<div class="row row--grid">
					<div class="col-sm-6">
						<p>Chaque gestion, document ou traitement sur Armado dispose d'un droit bien spécifique.</p>

						<div class="organization-usage__how-to">
							<h5>Comment ça marche ?</h5>
							<ul>
								<li>Définissez un nom et une couleur pour s’y retrouver</li>
								<li>Attribuez des droits associés à ce rôle</li>
								<li>Enfin, affectez ce tout nouveau rôle lorsque nécessaire !</li>
							</ul>
						</div>

						<a
							class="organization-usage__article-link"
							href="https://armado.fr/nos-actualites/organisation-sur-armado-les-roles"
							target="_blank"
							rel="noopener noreferrer">Plus d'informations sur armado.fr</a>
					</div>

					<div class="col-sm-6">
						<h5>Qu'y gagnez-vous ?</h5>

						<p>
							Afin de vous <b>éviter une contraignante ressaisie</b> à chaque nouvel utilisateur,
							Armado vous permet de créer un rôle et de <b>l'affecter facilement </b> à plusieurs utilisateurs.
						</p>

						<p>
							Sachant votre gestion des collaborateurs unique,
							Armado vous donne la liberté de créer autant de rôles que vous le souhaitez,
							et d'en affecter autant que nécessaire à chacun de vos utilisateurs.
							<br>
							Permettant ainsi un jeu de droits unique, pour chacun de vos besoins.
						</p>

						<o-button
							v-if="setup.self.hasOrganization && setup.self.can('OrganizationRoleCreate')"
							class="mt-xs"
							block
							outline
							color="info"
							@click="$router.push({ name: 'OrganizationRoles', query: { new: 1 } })">
							Créer un rôle
						</o-button>
					</div>
				</div>
			</div>
		</o-section>

		<o-section v-if="display.includes('tags')">
			<div class="organization-usage__section">
				<div class="row row--grid">
					<div class="col-sm-6">
						<o-label
							color="warning-alt"
							size="lg"
							outline>
							Les tags
						</o-label>

						<h4>Ou comment regrouper vos documents sous une même référence</h4>
					</div>
				</div>

				<div class="row row--grid">
					<div class="col-sm-6">
						<p>Tout comme les droits, votre classification des documents et vos processus sont uniques pour chacun d'entre vous.</p>

						<div class="organization-usage__how-to">
							<h5>Exemples d'usage</h5>
							<ul>
								<li>Répartition par service administratif (secrétariat, ressources humaines, direction...)</li>
								<li>Répartition par service de facturation (service pédiatrie, service cardiologie...)</li>
								<li>Répartition par site de production (chantier naval, chantier référencé par un identifiant interne...)</li>
							</ul>
						</div>

						<a
							class="organization-usage__article-link"
							href="https://armado.fr/nos-actualites/organisation-sur-armado-les-tags"
							target="_blank"
							rel="noopener noreferrer">Plus d'informations sur armado.fr</a>
					</div>

					<div class="col-sm-6">
						<h5>Qu'y gagnez-vous ?</h5>

						<p>
							Les Tags vous donnent la faculté de <b>réunir sous une même référence </b>
							ce qui compose votre quotidien dans le monde du travail temporaire.
							Mais, plus qu'un simple classeur,
							ceux-ci vous serviront également à restreindre
							la visibilité des documents qu'ils regroupent.
						</p>

						<p>
							En associant la flexibilité des droits et les
							capacités de cloisonnement des Tags, vous pourrez reproduire
							l'ensemble des fonctionnements qui caractérisent votre entreprise sur Armado.
						</p>

						<o-button
							v-if="setup.self.hasOrganization && setup.self.can('TagCreate')"
							class="mt-xs"
							block
							outline
							color="info"
							@click="$router.push({ name: 'OrganizationTags', query: { new: 1 } })">
							Créer un tag
						</o-button>
					</div>
				</div>
			</div>
		</o-section>

		<o-section v-if="display.includes('fusion')">
			<div class="organization-usage__section">
				<div class="flex ai-fs g-md">
					<div style="flex:2">
						<h2>Fusionnez des organisations</h2>

						<p>
							<o-label
								color="info"
								size="lg"
								outline>
								A quoi ça sert ?
							</o-label>
						</p>

						<p>
							Intégrez les sociétés d'une autre organisation <br>
							afin de compléter la vôtre et de travailler sous un référentiel unique.
						</p>

						<o-button
							v-if="setup.self.organization?.id"
							class="mt-sm"
							color="brand"
							@click="setup.openOrganizationFusionRequest()">
							Effectuer une demande de fusion
						</o-button>
						<o-alert
							v-else
							class="mt-sm"
							color="info"
							center>
							Pour effectuer une demande de fusion<br>vous devez d'abord créer votre propre organisation.
						</o-alert>
					</div>
					<img
						v-if="!setup.ui.onLayoutNavBottom"
						src="@/assets/img/OrganizationFusion.svg"
						style="flex:1">
				</div>
			</div>
		</o-section>
	</o-page>
</template>

<script setup lang="ts">
import OrganizationUsageSetupService from '@/setup/organization/OrganizationUsageSetupService';
const props = defineProps(OrganizationUsageSetupService.props);
const setup = new OrganizationUsageSetupService(props);
</script>

<style scoped lang="less">
@import '@/styles/variables.less';
@import '@/styles/mixins.less';

.orion-aside {
	.organization-usage {
			padding: 0;
	}
}

.organization-usage {
  background-color: white;
  border-radius: 0.5rem;
  padding: 4.25rem;
  font-size: 0.85rem;
  color: var(--grey-dark);

	@media @layoutNavBottom {
		padding: 1.5rem;
	}

  &__hero {
    display: flex;
    gap: var(--space-xl);

    &-poster {
      order: 2;
      flex: 2;

      @media @phone {
        order: 1;
      }
    }

    &-content {
      flex: 3;
    }
  }

  &__section {
    .orion-label + h4 {
      margin-top: 0.75rem;
    }
  }

  &__how-to {
    background-color: var(--grey-lighter);
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-top: 1.5rem;

    ul {
      padding-left: 1.25rem;
    }

    li {
      position: relative;
      display: block;
      padding: 0.25rem 0;

      &::before {
        .circle(0.4rem);
        content: '';
        position: absolute;
        left: -1rem;
        top: 0.75rem;
        background: var(--info);
      }
    }
  }

  &__article-link {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding-left: 0.5rem;
    margin-top: 0.5rem;
    text-decoration: none;
    color: var(--info);

    &::before {
      content: '\279F';
      transition: transform 0.5s ease-out;
      font-size: 1.25rem;
      transform: translate3d(0, 0.08rem, 0);
    }

    &::after {
      content: '';
      transition: transform 0.3s;
      display: block;
      position: absolute;
      bottom: 0;
      left: 0;
      right: -0.75rem;
      height: 1px;
      background-color: var(--info);
      transform: scale3d(0, 1, 1);
      transform-origin: left;
    }

    &:hover::before {
      transform: translate3d(0.3rem, 0.08rem, 0);
    }

    &:hover::after {
      transform: scale3d(1, 1, 1);
    }
  }

  > .orion-page__content {
    > .orion-section + .orion-section {
      position: relative;
      margin-top: 6rem;

      &::before {
        content: '';
        display: block;
        position: absolute;
        top: -3rem;
        left: 0;
        right: 0;
        height: 1px;
        background-color: var(--grey-light);
      }
    }
  }
}
</style>
